import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  IconButton,
  Text,
  Box,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from "@chakra-ui/react";
import { FaMinus, FaPlus } from "react-icons/fa";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// Default output dimensions match the coupon hero slot (~360x175 rendered at
// ~2x for crisp displays). Callers (e.g. the campaign hero picker) can pass
// their own dimensions + aspect; the aspect is locked in the UI so the user
// can't drag the crop region off-shape.
const DEFAULT_OUTPUT_WIDTH = 720;
const DEFAULT_OUTPUT_HEIGHT = 350;

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

const buildInitialCrop = (mediaWidth, mediaHeight, aspect) =>
  centerCrop(
    makeAspectCrop(
      { unit: "%", width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );

// Draws the user's crop selection onto an offscreen canvas at the target output
// size, then resolves with a JPEG blob suitable for upload. We multiply by the
// natural-to-displayed ratio so the crop maps back to original-image pixels
// regardless of how much we scaled in the preview.
const renderCroppedBlob = (image, crop, scale, outputWidth, outputHeight) =>
  new Promise((resolve, reject) => {
    if (!image || !crop || !crop.width || !crop.height) {
      reject(new Error("No crop selected"));
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const sx = (crop.x * scaleX) / scale;
    const sy = (crop.y * scaleY) / scale;
    const sWidth = (crop.width * scaleX) / scale;
    const sHeight = (crop.height * scaleY) / scale;

    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      image,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode cropped image"));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      0.92
    );
  });

const LogoCropperModal = ({
  isOpen,
  sourceUrl,
  onCancel,
  onApply,
  aspect,
  outputWidth = DEFAULT_OUTPUT_WIDTH,
  outputHeight = DEFAULT_OUTPUT_HEIGHT,
  title = "Crop your image",
  description,
}) => {
  const resolvedAspect = aspect || outputWidth / outputHeight;
  const resolvedDescription =
    description ||
    `Drag the corners to resize, or move the box to choose what part of your image will appear on the coupon. The crop is locked to the ${outputWidth}×${outputHeight} coupon shape.`;
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCrop(undefined);
      setCompletedCrop(null);
      setZoom(1);
      setIsApplying(false);
    }
  }, [isOpen]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(buildInitialCrop(width, height, resolvedAspect));
  };

  const adjustZoom = (delta) => {
    setZoom((current) => {
      const next = Math.round((current + delta) * 100) / 100;
      if (next < MIN_ZOOM) return MIN_ZOOM;
      if (next > MAX_ZOOM) return MAX_ZOOM;
      return next;
    });
  };

  const handleApply = async () => {
    if (!imgRef.current || !completedCrop) return;
    setIsApplying(true);
    try {
      const blob = await renderCroppedBlob(
        imgRef.current,
        completedCrop,
        zoom,
        outputWidth,
        outputHeight
      );
      const url = URL.createObjectURL(blob);
      onApply(blob, url);
    } catch (err) {
      console.error("Crop apply failed", err);
      setIsApplying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={3}>
            {resolvedDescription}
          </Text>
          {sourceUrl && (
            <Box
              bg="gray.100"
              borderRadius="md"
              p={2}
              maxH="60vh"
              overflow="auto"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={resolvedAspect}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={sourceUrl}
                  alt="To crop"
                  style={{
                    transform: `scale(${zoom})`,
                    transformOrigin: "top left",
                    maxWidth: "100%",
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </Box>
          )}
          <HStack mt={4} spacing={3}>
            <Text fontSize="sm" minW="60px">
              Zoom
            </Text>
            <IconButton
              aria-label="Zoom out"
              icon={<FaMinus />}
              size="sm"
              variant="outline"
              onClick={() => adjustZoom(-ZOOM_STEP)}
              isDisabled={zoom <= MIN_ZOOM}
            />
            <Slider
              flex="1"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              onChange={(v) => setZoom(v)}
              aria-label="Zoom level"
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
            <IconButton
              aria-label="Zoom in"
              icon={<FaPlus />}
              size="sm"
              variant="outline"
              onClick={() => adjustZoom(ZOOM_STEP)}
              isDisabled={zoom >= MAX_ZOOM}
            />
            <Text fontSize="sm" minW="40px" textAlign="right">
              {zoom.toFixed(1)}x
            </Text>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={handleApply}
            isDisabled={!completedCrop?.width}
            isLoading={isApplying}
          >
            Apply crop
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LogoCropperModal;
